import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UIStateEnum } from '../../enum/UIStateEnum';
import { ErrorCategoryService } from '../../services/error-category.service';
import { SnackbarService } from '../../services/snackbar.service';
import { IErrorCategory } from '../../interfaces/error/IErrorCategory';
import { ContainerComponent } from '@coreui/angular';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { HasPermissionDirective } from '../../directives/permissions/has-permission';
import { PermissionTypes } from '../../enum/PermissionTypes';
import { EditErrorCategoryTranslationComponent, EditErrorCategoryTranslationDialogData } from '../../components/dialogs/edit-error-category-translation/edit-error-category-translation.component';

@Component({
  selector: 'app-error-category-management',
  standalone: true,
  imports: [
    CommonModule,
    ContainerComponent,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatTableModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    FormsModule,
    HasPermissionDirective
  ],
  templateUrl: './error-category-management.component.html',
  styleUrl: './error-category-management.component.scss'
})
export class ErrorCategoryManagementComponent implements OnInit {
  public uiState = UIStateEnum.ShowLoading;
  public uiStateForTemplate = UIStateEnum;
  public permissionTypes = PermissionTypes;

  public categories: IErrorCategory[] = [];
  public filteredCategories: IErrorCategory[] = [];
  public searchText = '';

  public displayedColumns: string[] = ['value', 'name', 'message'];

  constructor(
    private errorCategoryService: ErrorCategoryService,
    private snackBarService: SnackbarService,
    private matDialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadCategories();
  }

  loadCategories() {
    this.uiState = UIStateEnum.ShowLoading;
    this.errorCategoryService.getAllCategories().subscribe({
      next: (res) => {
        this.categories = res.Categories;
        this.filteredCategories = res.Categories;
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackBarService.openErrorSnackBar('Failed to load error categories');
      }
    });
  }

  onSearchChange() {
    if (!this.searchText.trim()) {
      this.filteredCategories = this.categories;
      return;
    }

    const search = this.searchText.toLowerCase();
    this.filteredCategories = this.categories.filter(category => {
      // Check category name/description
      if (category.Name.toLowerCase().includes(search) ||
          category.Description.toLowerCase().includes(search)) {
        return true;
      }

      // Check translations
      for (const translation of Object.values(category.Translations)) {
        if (translation.toLowerCase().includes(search)) {
          return true;
        }
      }

      // Check error types
      for (const errorType of category.ErrorTypes) {
        if (errorType.Name.toLowerCase().includes(search) ||
            errorType.Value.toString().includes(search) ||
            errorType.Message.toLowerCase().includes(search)) {
          return true;
        }
      }

      return false;
    });
  }

  getTranslationLanguages(category: IErrorCategory): string[] {
    return Object.keys(category.Translations);
  }

  getTotalErrorTypes(): number {
    return this.categories.reduce((total, cat) => total + cat.ErrorTypes.length, 0);
  }

  openEditTranslationDialog(category: IErrorCategory, languageCode: string): void {
    const dialogData: EditErrorCategoryTranslationDialogData = {
      category,
      languageCode
    };

    this.matDialog.open(EditErrorCategoryTranslationComponent, {
      width: '550px',
      data: dialogData
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadCategories();
      }
    });
  }

  openAddTranslationDialog(category: IErrorCategory): void {
    const dialogData: EditErrorCategoryTranslationDialogData = {
      category
    };

    this.matDialog.open(EditErrorCategoryTranslationComponent, {
      width: '550px',
      data: dialogData
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadCategories();
      }
    });
  }

  canAddTranslation(category: IErrorCategory): boolean {
    const existingLanguages = Object.keys(category.Translations);
    return existingLanguages.length < 2;
  }
}
